import * as React from 'react';

// PACKAGES
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import ChooseCertification from '../ComponentForms/ChooseCertification';
import ExistingCertification from '../../../components/ExistingCertification';
import AddLaterRadio from '../../../components/AddLaterRadio';

// ICONS
import { ReactComponent as PlusIcon } from '../../../assets/svg/plus.svg';
import { ReactComponent as MinusIcon } from '../../../assets/svg/minus.svg';

// TYPES
import { CertificationTypes } from './CertificationStaff';
import { FileType } from '../../../components/FileUpload';
import { AccountExistingCertificationsCertification, DomainCertificationType } from '../../../api/models';

interface PartTwoTypes {
	certificationData: CertificationTypes;
	setFormSection: React.Dispatch<React.SetStateAction<string>>;
	setCertificationFormData: React.Dispatch<React.SetStateAction<CertificationTypes>>;
	existingCertifications: AccountExistingCertificationsCertification[];
	existingTeacherRegistration: AccountExistingCertificationsCertification[];
	existingWWCCRegistration: AccountExistingCertificationsCertification[];
}

interface certificationTemplateTypes {
	certificationType?: DomainCertificationType;
	state?: string;
	expiry?: string;
	certificationNumber?: string;
	userAcknowledgement?: boolean;
	attachments?: FileType[];
	createAttachments?: string[];
}

const PartTwo: React.FC<PartTwoTypes> = ({
	certificationData,
	setFormSection,
	setCertificationFormData,
	existingCertifications,
	existingTeacherRegistration,
	existingWWCCRegistration
}) => {
	const [isCertificationValid, setIsCertificationValid] = React.useState(false);

	// Set initial certification template
	const certificationTemplate: certificationTemplateTypes = {
		certificationType: undefined,
		state: '',
		expiry: '',
		certificationNumber: '',
		userAcknowledgement: false,
		attachments: [],
		createAttachments: []
	};

	const teacherRegFields = {
		teacherReg: false
	};

	const WWCCRegFields = {
		WWCCReg: false
	};

	// Set initial form values
	const initialFormValuesPartTwo = {
		accountCertification: certificationData.authorityToWork
			? [...certificationData.authorityToWork]
			: [{ ...certificationTemplate }],
		hasExistingCertification: isCertificationValid,
		hasValidExisitingCertificate: false,
		hasValidExisitingWWCCCertificate: false,
		hasValidExisitingTRCertificate: false,
		existingTeacherRegistration: existingTeacherRegistration?.map((item) => ({ ...item, ...teacherRegFields })) || [],
		existingWWCCRegistration: existingWWCCRegistration?.map((item) => ({ ...item, ...WWCCRegFields })) || [],
		selectedNewWWCC: false,
		selectedNewTeacherRegistration: false,
		useExistingCertifications: certificationData.authorityToWork?.[0].certificationType === undefined
	};

	//Submit part one form data to AddCertification
	const continueFormStepOne = (values: typeof initialFormValuesPartTwo) => {
		setFormSection('3');

		setCertificationFormData((prevState) => ({
			...prevState,

			authorityToWork: values.accountCertification.map((certificate) => {
				return certificate.certificationType === undefined
					? {}
					: {
							certificationType: certificate.certificationType,
							certificationNumber: certificate.certificationNumber,
							expiry: certificate.expiry,
							state: certificate.state,
							userAcknowledgement: certificate.userAcknowledgement,
							createAttachments: certificate.createAttachments,
							attachments: certificate.attachments || []
					  };
			})
		}));
	};

	// Validation
	const formValidationSchema = Yup.object().shape({
		accountCertification: Yup.array().when('hasExistingCertification', {
			is: true,
			then: Yup.array().when('useExistingCertifications', {
				is: true,
				then: Yup.array(),
				otherwise: Yup.array().of(
					Yup.object({
						certificationType: Yup.string().required('Required'),
						state: Yup.string().required('Required'),
						expiry: Yup.string().required('Required'),
						certificationNumber: Yup.string().required('Required'),
						createAttachments: Yup.array().min(1, 'Required').required(),
						userAcknowledgement: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
					})
				)
			}),
			otherwise: Yup.array().of(
				Yup.object({
					certificationType: Yup.string().required('Required'),
					state: Yup.string().required('Required'),
					expiry: Yup.string().required('Required'),
					certificationNumber: Yup.string().required('Required'),
					createAttachments: Yup.array().min(1, 'Required').required(),
					userAcknowledgement: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
				})
			)
		}),

		existingTeacherRegistration: Yup.array().when('hasValidExisitingTRCertificate', {
			is: true,
			then: Yup.array().of(
				Yup.object({
					teacherReg: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
				})
			)
		}),
		existingWWCCRegistration: Yup.array().when('hasValidExisitingWWCCCertificate', {
			is: true,
			then: Yup.array().of(
				Yup.object({
					WWCCReg: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
				})
			)
		})
	});

	// Check to see if certificate is valid or not
	const checkIfCertificationValid = () => {
		if (existingTeacherRegistration && existingTeacherRegistration.length > 0) {
			if (existingTeacherRegistration.some((item) => item.validCertification === true)) {
				return true;
			}
		}

		if (existingWWCCRegistration && existingWWCCRegistration.length > 0) {
			if (existingWWCCRegistration.some((item) => item.validCertification === true)) {
				return true;
			}
		}

		return false;
	};

	React.useEffect(() => {
		const isValid = checkIfCertificationValid() || false;
		setIsCertificationValid(isValid);
	}, []);

	return (
		<Formik
			initialValues={initialFormValuesPartTwo}
			onSubmit={continueFormStepOne}
			enableReinitialize={true}
			validationSchema={formValidationSchema}
			validateOnMount={true}
		>
			{(formik) => {
				React.useEffect(() => {
					formik.values.accountCertification.filter(
						(item) =>
							item.certificationType === DomainCertificationType.WorkingWithChildrenCheck
								? formik.setFieldValue('selectedNewWWCC', true)
								: item.certificationType === DomainCertificationType.TeacherRegistration
								? formik.setFieldValue('selectedNewTeacherRegistration', true)
								: formik.setFieldValue('selectedNewWWCC', false),
						formik.setFieldValue('selectedNewTeacherRegistration', false)
					);
				}, [formik.values.accountCertification]);

				return (
					<Form translate="yes">
						<div className="form-group">
							<label htmlFor="accountCertification" className="label">
								2. Please prove authority to work with and/or around children
							</label>

							<FieldArray name="accountCertification">
								{(props) => {
									const { push, remove, form } = props;
									const { values } = form;
									const { accountCertification } = values;

									return (
										<>
											<div>
												{accountCertification.map((accountCertification: any, index: any) => {
													return (
														<div key={index}>
															{formik.values.accountCertification[0].certificationType ===
																DomainCertificationType.WorkingWithChildrenCheck ||
															(formik.values.accountCertification[0].certificationType ===
																DomainCertificationType.TeacherRegistration &&
																index === 0) ? (
																<>
																	<Field
																		name={`accountCertification[0]`}
																		children={() => <ChooseCertification hasExisting={false} index={index} />}
																	/>

																	{formik.values.hasValidExisitingCertificate ? (
																		!formik.errors.accountCertification &&
																		formik.values.accountCertification[index].certificationType !== undefined &&
																		formik.values.accountCertification.length - 1 === index &&
																		!formik.values.useExistingCertifications ? (
																			<button
																				onClick={() => push({ ...certificationTemplate })}
																				type="button"
																				className="add-more-credentials full-width"
																			>
																				<PlusIcon className="icon" />
																				Add another credential
																			</button>
																		) : null
																	) : formik.values.accountCertification[index].certificationType &&
																	  formik.values.accountCertification.length - 1 === index &&
																	  !formik.values.useExistingCertifications ? (
																		<button
																			onClick={() => push({ ...certificationTemplate })}
																			type="button"
																			className="add-more-credentials full-width"
																		>
																			<PlusIcon className="icon" />
																			Add another credential
																		</button>
																	) : null}
																</>
															) : !formik.values.useExistingCertifications && index > 0 ? (
																<>
																	{index > 0 ? (
																		<div className="addition-container">
																			<label htmlFor="accountCertification" className="label">
																				Additional credentials
																			</label>

																			<button
																				onClick={() => remove(index)}
																				type="button"
																				className="remove-credentials full-width"
																			>
																				<MinusIcon className="icon" />
																				Remove
																			</button>
																		</div>
																	) : null}

																	<Field
																		name={`accountCertification[0]`}
																		children={() => <ChooseCertification hasExisting={false} index={index} />}
																	/>

																	{formik.values.accountCertification[index].certificationType &&
																	formik.values.accountCertification.length - 1 === index ? (
																		<button
																			onClick={() => push({ ...certificationTemplate })}
																			type="button"
																			className="add-more-credentials full-width"
																		>
																			<PlusIcon className="icon" />
																			Add another credential
																		</button>
																	) : null}
																</>
															) : (
																<Field
																	name={`accountCertification[0]`}
																	children={() => <ChooseCertification hasExisting={false} index={index} />}
																/>
															)}
														</div>
													);
												})}
											</div>
										</>
									);
								}}
							</FieldArray>
						</div>

						{isCertificationValid ? (
							<>
								<AddLaterRadio
									changeToUseExisting={true}
									onChange={(val: boolean) => {
										formik.setFieldValue('useExistingCertifications', val);
										formik.setFieldValue(`accountCertification[0].certificationType`, '');
									}}
									value={formik.values.useExistingCertifications}
								/>

								<div className="general-divider"></div>

								<ExistingCertification
									existingTeacherRegistration={existingTeacherRegistration}
									existingWWCCRegistration={existingWWCCRegistration}
								/>
							</>
						) : null}

						<Button type="submit" className="btn btn-primary full-width">
							Continue
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};

export default PartTwo;
